// Add MCP form. Test-connection button hits a placeholder endpoint that
// won't really do anything for non-mock URLs — fine for now, the same
// button gets a real round-trip once the gateway speaks the real protocol.

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import type { McpAuthType, McpCategory } from '@qualibot/shared';
import { Card } from '@/components/primitives/Card';
import { Btn } from '@/components/primitives/Btn';
import { PageContainer, PageHeader } from '@/components/PageHeader';
import { createMcpConnection } from '@/lib/mcps-api';
import { extractApiError } from '@/lib/api-client';

const CATEGORIES: McpCategory[] = [
  'DMS',
  'OCR',
  'CRM',
  'ERP',
  'CALENDAR',
  'WORKFLOW',
  'KNOWLEDGE',
  'HR',
  'RECRUITING',
  'CUSTOM',
];

const AUTH_TYPES: McpAuthType[] = ['API_KEY', 'OAUTH2', 'BASIC', 'SERVICE_ACCOUNT', 'NONE'];

export function McpAddPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState('Enterprise DMS MCP');
  const [category, setCategory] = useState<McpCategory>('DMS');
  const [description, setDescription] = useState('Production DMS via API gateway');
  const [endpointUrl, setEndpointUrl] = useState('https://api.company.com/mcp/dms');
  const [authType, setAuthType] = useState<McpAuthType>('API_KEY');
  const [secret, setSecret] = useState('demo-key-aaaa-bbbb-cccc');
  const [enableLogging, setEnableLogging] = useState(true);
  const [requireApprovalHighRisk, setRequireApprovalHighRisk] = useState(true);

  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: createMcpConnection,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['mcps'] });
      navigate(`/mcp-tools/${created.id}`);
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      name: name.trim(),
      category,
      description: description.trim() || undefined,
      endpointUrl: endpointUrl.trim(),
      authType,
      authConfig:
        authType === 'NONE'
          ? undefined
          : { apiKey: secret, _logging: enableLogging ? 'on' : 'off', _approveHighRisk: requireApprovalHighRisk ? 'on' : 'off' },
    });
  }

  return (
    <PageContainer>
      <div className="max-w-[760px]">
        <PageHeader
          title="Add MCP"
          description="Connect a new external system to Qualibot."
        />

        <form onSubmit={handleSubmit}>
          <Card>
            <FieldText label="MCP name" value={name} onChange={setName} />
            <div className="grid grid-cols-2 gap-3.5 mt-3.5">
              <FieldSelect
                label="Category"
                value={category}
                onChange={(v) => setCategory(v as McpCategory)}
                options={CATEGORIES}
              />
              <FieldSelect
                label="Authentication"
                value={authType}
                onChange={(v) => setAuthType(v as McpAuthType)}
                options={AUTH_TYPES}
              />
            </div>
            <FieldText
              label="Endpoint URL"
              value={endpointUrl}
              onChange={setEndpointUrl}
              hint="Use mock:// to create a stub connection for development."
              className="mt-3.5"
            />
            <FieldText
              label="Description"
              value={description}
              onChange={setDescription}
              className="mt-3.5"
            />
            {authType !== 'NONE' && (
              <FieldText
                label="API key / secret"
                value={secret}
                onChange={setSecret}
                type="password"
                hint="Stored server-side only. UI shows the last 4 characters masked."
                className="mt-3.5"
              />
            )}
          </Card>

          <Card className="mt-3.5">
            <div className="font-semibold text-[13.5px] mb-3">Connection options</div>
            <div className="flex flex-col gap-2.5">
              <Check
                label="Enable logging on every tool call"
                checked={enableLogging}
                onChange={setEnableLogging}
              />
              <Check
                label="Require approval for high-risk tools"
                checked={requireApprovalHighRisk}
                onChange={setRequireApprovalHighRisk}
              />
            </div>
          </Card>

          {error && (
            <div
              role="alert"
              className="mt-3.5 px-3 py-2 rounded-md text-[12.5px]"
              style={{ background: 'var(--err-soft)', color: 'var(--err)' }}
            >
              {error}
            </div>
          )}

          <div className="flex items-center justify-between mt-5">
            <Btn type="button" onClick={() => navigate('/mcp-tools')}>
              Cancel
            </Btn>
            <Btn
              type="submit"
              kind="primary"
              icon={Save}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Saving…' : 'Save MCP'}
            </Btn>
          </div>
        </form>
      </div>
    </PageContainer>
  );
}

function FieldText({
  label,
  value,
  onChange,
  hint,
  type = 'text',
  className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
  type?: string;
  className?: string;
}) {
  return (
    <div className={['flex flex-col gap-1.5', className].filter(Boolean).join(' ')}>
      <label className="text-[12px] font-semibold text-text-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 px-3 rounded-[10px] border border-border-strong bg-surface text-[13.5px] outline-none focus:border-accent"
      />
      {hint && <div className="text-[11.5px] text-text-3">{hint}</div>}
    </div>
  );
}

function FieldSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[12px] font-semibold text-text-2">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 px-3 rounded-[10px] border border-border-strong bg-surface text-[13.5px] outline-none focus:border-accent"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2.5 text-[13.5px] cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: 'var(--accent)', width: 15, height: 15, margin: 0 }}
      />
      {label}
    </label>
  );
}
